import EventCard from "./EventCard";
import React, { useState, useEffect } from 'react';
import { useAppData } from './context/DataContext';
import { Link, useParams } from "react-router-dom";
import Navbar from './Navbar';
import Footer from './Footer';
import OrganizerNavbar from "./OrganizerNavbar.js";
import { ReactComponent as Avatar } from "./IconUmmahfy.svg";
import { Segmented, Upload, Input, Button, message, Spin, ConfigProvider } from 'antd';
import { ReactComponent as NoEvents } from './NoEvents.svg';
import { EditOutlined, CheckOutlined, CloseOutlined, UserOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
const secrets = require('./secrets.js');
const api = secrets.server_url;

const Profile = () => {
  const {
    informationWithPastEvents,
    loggedInUser,
    loggedInEmail,
    userInfo,
    setAppData
  } = useAppData();

  // Get user ID from URL params
  const { id } = useParams();

  const [user, setUser] = useState(loggedInUser.data);
  const [email, setEmail] = useState(loggedInEmail.data);
  const [userDetails, setUserDetails] = useState(userInfo ? userInfo : null);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('Upcoming');  // Add this for the toggle
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Check if this is the current user's profile
  const isOwnProfile = userInfo && (!id || id === userInfo._id);

  // If we have an ID parameter, fetch that specific user
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!id || (userInfo && id === userInfo._id)) {
        // If this is the current user's profile, use the cached data
        setUserDetails(userInfo);
        setEditedName(userInfo?.name || userInfo?.username || 'User');
        setProfilePicture(userInfo?.profilePicture || null);
        return;
      }
      
      try {
        setLoading(true);
        const response = await axios.get(`${api}user/${id}`, {
          withCredentials: true,
          headers: {
            'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
          }
        });
        
        if (response.data.success) {
          setUserDetails(response.data.data);
          setEditedName(response.data.data.name || response.data.data.username || 'User');
          setProfilePicture(response.data.data.profilePicture || null);
        } else {
          message.error('Failed to load user profile');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        message.error('Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [id, userInfo]);

  // Function to upload image
  const uploadImage = async (imageFile) => {
    try {
      // Create FormData object
      const formData = new FormData();
      formData.append('image', imageFile);

      // Make the request
      const response = await axios.post(`${api}uploadImage`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      return response.data.imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error.response?.data?.message || error.message);
      throw error;
    }
  };

  // Handle profile picture upload
  const handleProfilePictureUpload = async (info) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }
    
    if (info.file.status === 'done') {
      try {
        const imgUrl = await uploadImage(info.file.originFileObj);
        setProfilePicture(imgUrl);
        message.success('Profile picture uploaded successfully');
      } catch (error) {
        console.error('Error uploading profile picture:', error);
        message.error('Failed to upload profile picture');
      } finally {
        setUploading(false);
      }
    }
  };

  // Handle edit toggle
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset values
      setEditedName(userDetails?.name || userDetails?.username || 'User');
      setProfilePicture(userDetails?.profilePicture || null);
    }
    setIsEditing(!isEditing);
  };

  // Handle save profile
  const handleSaveProfile = async () => {
    try {
      setUploading(true);
      
      // Prepare update data
      const updateData = {
        name: editedName,
      };
      
      // Include profile picture if it changed
      if (profilePicture !== userDetails?.profilePicture) {
        updateData.profilePicture = profilePicture;
      }

      // Use the ID from URL params or fallback to the userDetails._id if it's undefined
      const userId = id || (userDetails && userDetails._id);
      
      console.log('Updating user profile with ID:', userId);
      console.log('Update data:', updateData);

      if (!userId) {
        throw new Error('User ID is missing');
      }

      // Call API to update user profile - using PUT with the user ID in the URL
      const response = await axios.put(
        `${api}user/${userId}`,
        updateData,
        {
          withCredentials: true,
          headers: {
            'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Update response:', response.data);
      
      if (response.data.data) {
        // Update the user details in state and context
        const updatedUserDetails = {
          ...userDetails,
          name: editedName,
          profilePicture: profilePicture
        };
        
        setUserDetails(updatedUserDetails);
        
        // Also update the app context if this is the current user
        if (isOwnProfile) {
          setAppData(prev => ({
            ...prev,
            userInfo: {
              ...prev.userInfo,
              name: editedName,
              profilePicture: profilePicture
            }
          }));
          
          // Also update localStorage to ensure data persists on page reload
          try {
            const currentUserStr = localStorage.getItem('currentUser');
            if (currentUserStr) {
              const currentUser = JSON.parse(currentUserStr);
              currentUser.name = editedName;
              if (profilePicture) {
                currentUser.profilePicture = profilePicture;
              }
              localStorage.setItem('currentUser', JSON.stringify(currentUser));
              console.log('Updated user data in localStorage');
            }
          } catch (storageError) {
            console.error('Error updating localStorage:', storageError);
          }
        }
        
        message.success('Profile updated successfully');
        setIsEditing(false);
      } else {
        message.error('Failed to update profile: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      console.error('Error details:', error.response?.data || 'No detailed error info');
      message.error('Failed to update profile: ' + errorMsg);
    } finally {
      setUploading(false);
    }
  };

  function createCard(info) {
    return (
      <Link key={info.event_id} to={`/event/${info.event_id}`} className="card-link">
        <EventCard
          imgurl={info.img}
          event={info.name}
          masjid={info.organization}
          altdate={info.timeArray[0]}
          description={info.description}
          age={info.age_range}
          frequency={info.frequency}
          price={info.price}
          gender={info.gender}
          registered={info.registered}
          time={info.time}
          tags={info.tags}
        />
      </Link>
    );
  }

  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const searchIds = (userDetails && userDetails.signedUpEvents) 
    ? userDetails.signedUpEvents.filter(event => !isNaN(event)) 
    : [];

  function filterEventsByIds(events, searchIds) {
    return events.filter(event => searchIds.includes(event.event_id.toString()));
  }

  const filteredEventsByIds = filterEventsByIds(informationWithPastEvents, searchIds);
  // Separate past and upcoming events
  const pastEvents = filteredEventsByIds.filter(event => {
    const eventDate = event.start_datetime ? event.start_datetime : event.dates[0];
    return new Date(eventDate) < currentDate || event.past === true;
  });

  const upcomingEvents = filteredEventsByIds.filter(event => event.past !== true);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
          <Spin size="large" />
        </div>
        <Footer />
      </>
    );
  }

  if (!userDetails) {
    return (
      <>
        <Navbar />
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>Error loading user details.</div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="wrapper">
        <Navbar />
        <div className="profile-container">
          <div className="profile-header">
            {isEditing ? (
              <div className="profile-image-edit">
                <Upload
                  name="profilePicture"
                  listType="picture-card"
                  className="profile-picture-uploader"
                  showUploadList={false}
                  action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188" // Dummy URL, we handle upload manually
                  beforeUpload={() => false}
                  onChange={handleProfilePictureUpload}
                >
                  {profilePicture ? (
                    <img 
                      src={profilePicture} 
                      alt="Profile" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} 
                    />
                  ) : (
                    <div>
                      {uploading ? <Spin /> : <UploadOutlined />}
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
              </div>
            ) : (
              profilePicture ? (
                <img 
                  src={profilePicture} 
                  alt="Profile" 
                  className="profile-image" 
                  style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '50%' }} 
                />
              ) : (
                <Avatar className="profile-image" />
              )
            )}
            <div className="profile-details">
              {isEditing ? (
                <div className="name-edit-container">
                  <Input
                    value={editedName}
                    onChange={e => setEditedName(e.target.value)}
                    placeholder="Your Name"
                    style={{ marginBottom: '10px' }}
                  />
                  <div className="edit-actions">
                    <Button
                      icon={<CheckOutlined />}
                      type="primary"
                      onClick={handleSaveProfile}
                      loading={uploading}
                      style={{ marginRight: '8px' }}
                    >
                      Save
                    </Button>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={handleEditToggle}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="name-container" style={{ display: 'flex', alignItems: 'center' }}>
                    <h2>{userDetails?.name || userDetails?.username || 'User'}</h2>
                    {isOwnProfile && (
                      <Button
                        icon={<EditOutlined />}
                        type="text"
                        onClick={handleEditToggle}
                        style={{ marginLeft: '8px' }}
                      />
                    )}
                  </div>
                  <p>{userDetails?.signedUpEvents?.length || 0} Events signed up</p>
                </>
              )}
            </div>
          </div>

          <div className="org-title-toggle">
            <h2 className="registered-events-title">Registered Events</h2>
            <ConfigProvider
              theme={{
                components: {
                  Segmented: {
                    trackBg: 'rgba(255, 255, 255, 0.075)',
                    itemColor: '#ccc',
                    itemSelectedColor: '#fff',
                    itemHoverColor: '#fff',
                    borderRadius: 4,
                    borderRadiusLG: 4,
                    itemSelectedBg: 'rgba(255, 255, 255, 0.075)',
                    controlHeight: 36,
                  },
                },
              }}
            >
            <Segmented
              size="large"
              className="org-toggle"
              options={['Upcoming', 'Past']}
              value={value}
              onChange={setValue}
            />
            </ConfigProvider>
          </div>

          {value === 'Upcoming' ? (
            upcomingEvents.length > 0 ? (
              upcomingEvents.map(createCard)
            ) : (
              <div className="no-events-message">
                <NoEvents className="prof-no-events" />
              </div>
            )
          ) : (
            pastEvents.length > 0 ? (
              pastEvents.map(createCard)
            ) : (
              <div className="no-events-message">
                <NoEvents className="prof-no-events" />
              </div>
            )
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Profile;